<template>
  <div id="pacman-game">
    <div class="game-header">
      <div class="score">SCORE: {{ score }}</div>
      <div class="lives">LIVES: {{ lives }}</div>
      <div class="level">LEVEL: {{ level }}</div>
    </div>
    
    <div class="game-container">
      <canvas 
        ref="gameCanvas" 
        width="448" 
        height="496"
        @keydown="handleKeyPress"
        tabindex="0"
      ></canvas>
    </div>
    
    <div class="game-controls">
      <div class="instructions">
        <p>Use arrow keys to move Pac-Man</p>
        <p>Eat all dots to complete the level</p>
        <p>Avoid ghosts unless you have power pellets!</p>
      </div>
      <button @click="startGame" :disabled="gameRunning">Start Game</button>
      <button @click="pauseGame" :disabled="!gameRunning">Pause</button>
      <button @click="resetGame">Reset</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PacmanGame',
  data() {
    return {
      canvas: null,
      ctx: null,
      gameRunning: false,
      gamePaused: false,
      score: 0,
      lives: 3,
      level: 1,
      
      // Game dimensions
      tileSize: 16,
      mazeWidth: 28,
      mazeHeight: 31,
      
      // Pac-Man properties
      pacman: {
        x: 14,
        y: 23,
        direction: 0, // 0: right, 1: down, 2: left, 3: up
        nextDirection: 0,
        mouthAngle: 0,
        mouthDirection: 1
      },
      
      // Ghosts
      ghosts: [
        { x: 13, y: 11, direction: 0, color: '#FF0000', mode: 'chase', lastDirectionChange: 0 }, // Red
        { x: 14, y: 11, direction: 0, color: '#FFB8FF', mode: 'chase', lastDirectionChange: 0 }, // Pink
        { x: 15, y: 11, direction: 0, color: '#00FFFF', mode: 'chase', lastDirectionChange: 0 }, // Cyan
        { x: 16, y: 11, direction: 0, color: '#FFB852', mode: 'chase', lastDirectionChange: 0 }  // Orange
      ],
      
      // Game state
      dots: [],
      powerPellets: [],
      frightenedMode: false,
      frightenedTimer: 0,
      animationId: null,
      frameCount: 0,
      
      // Maze layout (1 = wall, 0 = path, 2 = dot, 3 = power pellet)
      maze: [
        [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
        [1,2,2,2,2,2,2,2,2,2,2,2,2,1,1,2,2,2,2,2,2,2,2,2,2,2,2,1],
        [1,2,1,1,1,1,2,1,1,1,1,1,2,1,1,2,1,1,1,1,1,2,1,1,1,1,2,1],
        [1,3,1,0,0,1,2,1,0,0,0,1,2,1,1,2,1,0,0,0,1,2,1,0,0,1,3,1],
        [1,2,1,1,1,1,2,1,1,1,1,1,2,1,1,2,1,1,1,1,1,2,1,1,1,1,2,1],
        [1,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,1],
        [1,2,1,1,1,1,2,1,1,2,1,1,1,1,1,1,1,1,2,1,1,2,1,1,1,1,2,1],
        [1,2,2,2,2,2,2,1,1,2,2,2,2,1,1,2,2,2,2,1,1,2,2,2,2,2,2,1],
        [1,1,1,1,1,1,2,1,1,1,1,1,0,1,1,0,1,1,1,1,1,2,1,1,1,1,1,1],
        [0,0,0,0,0,1,2,1,1,0,0,0,0,0,0,0,0,0,0,1,1,2,1,0,0,0,0,0],
        [0,0,0,0,0,1,2,1,1,0,1,1,1,0,0,1,1,1,0,1,1,2,1,0,0,0,0,0],
        [1,1,1,1,1,1,2,1,1,0,1,0,0,0,0,0,0,1,0,1,1,2,1,1,1,1,1,1],
        [0,0,0,0,0,0,2,0,0,0,1,0,0,0,0,0,0,1,0,0,0,2,0,0,0,0,0,0],
        [1,1,1,1,1,1,2,1,1,0,1,0,0,0,0,0,0,1,0,1,1,2,1,1,1,1,1,1],
        [0,0,0,0,0,1,2,1,1,0,1,1,1,1,1,1,1,1,0,1,1,2,1,0,0,0,0,0],
        [0,0,0,0,0,1,2,1,1,0,0,0,0,0,0,0,0,0,0,1,1,2,1,0,0,0,0,0],
        [1,1,1,1,1,1,2,1,1,0,1,1,1,1,1,1,1,1,0,1,1,2,1,1,1,1,1,1],
        [1,2,2,2,2,2,2,2,2,2,2,2,2,1,1,2,2,2,2,2,2,2,2,2,2,2,2,1],
        [1,2,1,1,1,1,2,1,1,1,1,1,2,1,1,2,1,1,1,1,1,2,1,1,1,1,2,1],
        [1,2,2,2,1,1,2,2,2,2,2,2,2,0,0,2,2,2,2,2,2,2,1,1,2,2,2,1],
        [1,1,1,2,1,1,2,1,1,2,1,1,1,1,1,1,1,1,2,1,1,2,1,1,2,1,1,1],
        [1,1,1,2,1,1,2,1,1,2,1,1,1,1,1,1,1,1,2,1,1,2,1,1,2,1,1,1],
        [1,2,2,2,2,2,2,1,1,2,2,2,2,1,1,2,2,2,2,1,1,2,2,2,2,2,2,1],
        [1,2,1,1,1,1,1,1,1,1,1,1,2,1,1,2,1,1,1,1,1,1,1,1,1,1,2,1],
        [1,2,1,1,1,1,1,1,1,1,1,1,2,1,1,2,1,1,1,1,1,1,1,1,1,1,2,1],
        [1,3,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,2,3,1],
        [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1]
      ]
    };
  },
  
  mounted() {
    this.initGame();
    this.setupEventListeners();
  },
  
  beforeUnmount() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
    }
  },
  
  methods: {
    initGame() {
      this.canvas = this.$refs.gameCanvas;
      this.ctx = this.canvas.getContext('2d');
      this.canvas.focus();
      this.initializeDots();
      this.drawMaze();
    },
    
    setupEventListeners() {
      document.addEventListener('keydown', this.handleKeyPress);
    },
    
    handleKeyPress(event) {
      if (!this.gameRunning || this.gamePaused) return;
      
      switch(event.key) {
        case 'ArrowUp':
          this.pacman.nextDirection = 3;
          break;
        case 'ArrowDown':
          this.pacman.nextDirection = 1;
          break;
        case 'ArrowLeft':
          this.pacman.nextDirection = 2;
          break;
        case 'ArrowRight':
          this.pacman.nextDirection = 0;
          break;
      }
    },
    
    initializeDots() {
      this.dots = [];
      this.powerPellets = [];
      
      for (let y = 0; y < this.maze.length; y++) {
        for (let x = 0; x < this.maze[y].length; x++) {
          if (this.maze[y][x] === 2) {
            this.dots.push({ x, y });
          } else if (this.maze[y][x] === 3) {
            this.powerPellets.push({ x, y });
          }
        }
      }
    },
    
    drawMaze() {
      this.ctx.fillStyle = '#0000AA';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
      
      for (let y = 0; y < this.maze.length; y++) {
        for (let x = 0; x < this.maze[y].length; x++) {
          if (this.maze[y][x] === 1) {
            this.ctx.fillStyle = '#0000FF';
            this.ctx.fillRect(x * this.tileSize, y * this.tileSize, this.tileSize, this.tileSize);
          }
        }
      }
    },
    
    drawDots() {
      this.ctx.fillStyle = '#FFFF00';
      this.dots.forEach(dot => {
        this.ctx.beginPath();
        this.ctx.arc(
          dot.x * this.tileSize + this.tileSize / 2,
          dot.y * this.tileSize + this.tileSize / 2,
          2,
          0,
          2 * Math.PI
        );
        this.ctx.fill();
      });
    },
    
    drawPowerPellets() {
      this.ctx.fillStyle = '#FFFF00';
      this.powerPellets.forEach(pellet => {
        this.ctx.beginPath();
        this.ctx.arc(
          pellet.x * this.tileSize + this.tileSize / 2,
          pellet.y * this.tileSize + this.tileSize / 2,
          6,
          0,
          2 * Math.PI
        );
        this.ctx.fill();
      });
    },
    
    drawPacman() {
      const centerX = this.pacman.x * this.tileSize + this.tileSize / 2;
      const centerY = this.pacman.y * this.tileSize + this.tileSize / 2;
      const radius = this.tileSize / 2 - 1;
      
      this.ctx.fillStyle = '#FFFF00';
      this.ctx.beginPath();
      
      let startAngle, endAngle;
      switch (this.pacman.direction) {
        case 0: // Right
          startAngle = this.pacman.mouthAngle * Math.PI / 180;
          endAngle = (360 - this.pacman.mouthAngle) * Math.PI / 180;
          break;
        case 1: // Down
          startAngle = (90 + this.pacman.mouthAngle) * Math.PI / 180;
          endAngle = (450 - this.pacman.mouthAngle) * Math.PI / 180;
          break;
        case 2: // Left
          startAngle = (180 + this.pacman.mouthAngle) * Math.PI / 180;
          endAngle = (540 - this.pacman.mouthAngle) * Math.PI / 180;
          break;
        case 3: // Up
          startAngle = (270 + this.pacman.mouthAngle) * Math.PI / 180;
          endAngle = (630 - this.pacman.mouthAngle) * Math.PI / 180;
          break;
      }
      
      this.ctx.arc(centerX, centerY, radius, startAngle, endAngle);
      this.ctx.lineTo(centerX, centerY);
      this.ctx.fill();
    },
    
    drawGhosts() {
      this.ghosts.forEach(ghost => {
        const centerX = ghost.x * this.tileSize + this.tileSize / 2;
        const centerY = ghost.y * this.tileSize + this.tileSize / 2;
        const radius = this.tileSize / 2 - 1;
        
        // Ghost body - improved frightened mode colors
        if (this.frightenedMode) {
          // Flash between blue and white when frightened
          const flashColor = Math.floor(this.frameCount / 10) % 2 === 0 ? '#0000FF' : '#FFFFFF';
          this.ctx.fillStyle = flashColor;
        } else {
          this.ctx.fillStyle = ghost.color;
        }
        
        this.ctx.beginPath();
        this.ctx.arc(centerX, centerY - 2, radius, Math.PI, 0);
        this.ctx.rect(centerX - radius, centerY - 2, radius * 2, radius + 2);
        this.ctx.fill();
        
        // Ghost eyes
        this.ctx.fillStyle = '#FFFFFF';
        this.ctx.beginPath();
        this.ctx.arc(centerX - 3, centerY - 4, 2, 0, 2 * Math.PI);
        this.ctx.arc(centerX + 3, centerY - 4, 2, 0, 2 * Math.PI);
        this.ctx.fill();
        
        // Ghost pupils - different for frightened mode
        if (this.frightenedMode) {
          // Scared eyes - look down
          this.ctx.fillStyle = '#000000';
          this.ctx.beginPath();
          this.ctx.arc(centerX - 3, centerY - 2, 1, 0, 2 * Math.PI);
          this.ctx.arc(centerX + 3, centerY - 2, 1, 0, 2 * Math.PI);
          this.ctx.fill();
        } else {
          // Normal eyes
          this.ctx.fillStyle = '#000000';
          this.ctx.beginPath();
          this.ctx.arc(centerX - 3, centerY - 4, 1, 0, 2 * Math.PI);
          this.ctx.arc(centerX + 3, centerY - 4, 1, 0, 2 * Math.PI);
          this.ctx.fill();
        }
      });
    },
    
    canMove(x, y, direction) {
      let newX = x;
      let newY = y;
      
      switch (direction) {
        case 0: newX++; break; // Right
        case 1: newY++; break; // Down
        case 2: newX--; break; // Left
        case 3: newY--; break; // Up
      }
      
      // Wrap around tunnel
      if (newX < 0) newX = this.mazeWidth - 1;
      if (newX >= this.mazeWidth) newX = 0;
      
      return newY >= 0 && newY < this.maze.length && this.maze[newY][newX] !== 1;
    },
    
    getDirectionTowardsPacman(ghostX, ghostY) {
      const dx = this.pacman.x - ghostX;
      const dy = this.pacman.y - ghostY;
      
      // Prioritize the direction with the larger difference
      if (Math.abs(dx) > Math.abs(dy)) {
        return dx > 0 ? 0 : 2; // Right or Left
      } else {
        return dy > 0 ? 1 : 3; // Down or Up
      }
    },
    
    getDirectionAwayFromPacman(ghostX, ghostY) {
      const dx = this.pacman.x - ghostX;
      const dy = this.pacman.y - ghostY;
      
      // Prioritize the direction with the larger difference (opposite)
      if (Math.abs(dx) > Math.abs(dy)) {
        return dx > 0 ? 2 : 0; // Left or Right
      } else {
        return dy > 0 ? 3 : 1; // Up or Down
      }
    },
    
    getDirectionTowardsTarget(ghostX, ghostY, targetX, targetY) {
      const dx = targetX - ghostX;
      const dy = targetY - ghostY;
      
      // Prioritize the direction with the larger difference
      if (Math.abs(dx) > Math.abs(dy)) {
        return dx > 0 ? 0 : 2; // Right or Left
      } else {
        return dy > 0 ? 1 : 3; // Down or Up
      }
    },
    
    movePacman() {
      // Try to change direction
      if (this.canMove(this.pacman.x, this.pacman.y, this.pacman.nextDirection)) {
        this.pacman.direction = this.pacman.nextDirection;
      }
      
      // Move in current direction
      if (this.canMove(this.pacman.x, this.pacman.y, this.pacman.direction)) {
        switch (this.pacman.direction) {
          case 0: this.pacman.x++; break; // Right
          case 1: this.pacman.y++; break; // Down
          case 2: this.pacman.x--; break; // Left
          case 3: this.pacman.y--; break; // Up
        }
        
        // Wrap around tunnel
        if (this.pacman.x < 0) this.pacman.x = this.mazeWidth - 1;
        if (this.pacman.x >= this.mazeWidth) this.pacman.x = 0;
      }
      
      // Animate mouth
      this.pacman.mouthAngle += this.pacman.mouthDirection * 5;
      if (this.pacman.mouthAngle >= 30 || this.pacman.mouthAngle <= 0) {
        this.pacman.mouthDirection *= -1;
      }
    },
    
    moveGhosts() {
      this.ghosts.forEach((ghost, index) => {
        // Get all possible directions
        const possibleDirections = [];
        for (let dir = 0; dir < 4; dir++) {
          if (this.canMove(ghost.x, ghost.y, dir)) {
            possibleDirections.push(dir);
          }
        }
        
        if (possibleDirections.length > 0) {
          let newDirection = ghost.direction;
          
          // Add randomness to ghost behavior
          const randomFactor = Math.random();
          
          // Force direction change occasionally for more dynamic movement
          const shouldChangeDirection = Math.random() < 0.15 || 
            (this.frameCount - ghost.lastDirectionChange > 60); // Change every ~1 second
          
          // Different behavior for each ghost with more randomization
          switch (index) {
            case 0: // Red ghost - aggressive chaser with some randomness
              if (!this.frightenedMode) {
                if (randomFactor < 0.7) {
                  newDirection = this.getDirectionTowardsPacman(ghost.x, ghost.y);
                } else {
                  // Random movement 30% of the time
                  newDirection = possibleDirections[Math.floor(Math.random() * possibleDirections.length)];
                }
              } else {
                newDirection = this.getDirectionAwayFromPacman(ghost.x, ghost.y);
              }
              break;
              
            case 1: // Pink ghost - ambush behavior with more randomness
              if (!this.frightenedMode) {
                if (randomFactor < 0.6) {
                  const targetX = this.pacman.x + (this.pacman.direction === 0 ? 4 : this.pacman.direction === 2 ? -4 : 0);
                  const targetY = this.pacman.y + (this.pacman.direction === 1 ? 4 : this.pacman.direction === 3 ? -4 : 0);
                  newDirection = this.getDirectionTowardsTarget(ghost.x, ghost.y, targetX, targetY);
                } else {
                  // Random movement 40% of the time
                  newDirection = possibleDirections[Math.floor(Math.random() * possibleDirections.length)];
                }
              } else {
                newDirection = this.getDirectionAwayFromPacman(ghost.x, ghost.y);
              }
              break;
              
            case 2: // Cyan ghost - very random with occasional intelligence
              if (!this.frightenedMode) {
                if (randomFactor < 0.4) {
                  newDirection = this.getDirectionTowardsPacman(ghost.x, ghost.y);
                } else if (randomFactor < 0.6) {
                  // Try to move in a different direction than current
                  const oppositeDir = (ghost.direction + 2) % 4;
                  if (possibleDirections.includes(oppositeDir)) {
                    newDirection = oppositeDir;
                  } else {
                    newDirection = possibleDirections[Math.floor(Math.random() * possibleDirections.length)];
                  }
                } else {
                  // Completely random movement 40% of the time
                  newDirection = possibleDirections[Math.floor(Math.random() * possibleDirections.length)];
                }
              } else {
                newDirection = this.getDirectionAwayFromPacman(ghost.x, ghost.y);
              }
              break;
              
            case 3: // Orange ghost - cautious with distance-based behavior
              if (!this.frightenedMode) {
                const distance = Math.abs(ghost.x - this.pacman.x) + Math.abs(ghost.y - this.pacman.y);
                if (distance > 8) {
                  if (randomFactor < 0.8) {
                    newDirection = this.getDirectionTowardsPacman(ghost.x, ghost.y);
                  } else {
                    newDirection = possibleDirections[Math.floor(Math.random() * possibleDirections.length)];
                  }
                } else {
                  if (randomFactor < 0.7) {
                    newDirection = this.getDirectionAwayFromPacman(ghost.x, ghost.y);
                  } else {
                    newDirection = possibleDirections[Math.floor(Math.random() * possibleDirections.length)];
                  }
                }
              } else {
                newDirection = this.getDirectionAwayFromPacman(ghost.x, ghost.y);
              }
              break;
          }
          
          // Validate the new direction and ensure it's different from current if possible
          if (possibleDirections.includes(newDirection)) {
            // Sometimes keep current direction for more natural movement
            if (Math.random() < 0.2 && possibleDirections.includes(ghost.direction) && !shouldChangeDirection) {
              // Keep current direction 20% of the time
            } else {
              ghost.direction = newDirection;
              ghost.lastDirectionChange = this.frameCount;
            }
          } else if (possibleDirections.length > 0) {
            // Fallback to random direction if target direction not possible
            ghost.direction = possibleDirections[Math.floor(Math.random() * possibleDirections.length)];
            ghost.lastDirectionChange = this.frameCount;
          }
          
          // Move ghost
          if (this.canMove(ghost.x, ghost.y, ghost.direction)) {
            switch (ghost.direction) {
              case 0: ghost.x++; break;
              case 1: ghost.y++; break;
              case 2: ghost.x--; break;
              case 3: ghost.y--; break;
            }
            
            // Wrap around tunnel
            if (ghost.x < 0) ghost.x = this.mazeWidth - 1;
            if (ghost.x >= this.mazeWidth) ghost.x = 0;
          }
        }
      });
    },
    
    checkCollisions() {
      // Check dot collisions
      this.dots = this.dots.filter(dot => {
        if (dot.x === this.pacman.x && dot.y === this.pacman.y) {
          this.score += 10;
          return false;
        }
        return true;
      });
      
      // Check power pellet collisions
      this.powerPellets = this.powerPellets.filter(pellet => {
        if (pellet.x === this.pacman.x && pellet.y === this.pacman.y) {
          this.score += 50;
          this.frightenedMode = true;
          this.frightenedTimer = 300; // 5 seconds at 60fps
          return false;
        }
        return true;
      });
      
      // Check ghost collisions - improved collision detection
      this.ghosts.forEach((ghost, index) => {
        // More precise collision detection
        if (ghost.x === this.pacman.x && ghost.y === this.pacman.y) {
          if (this.frightenedMode) {
            // Eat ghost - improved scoring and respawn
            this.score += 200;
            // Reset ghost to starting position
            ghost.x = 13 + index; // Spread them out
            ghost.y = 11;
            ghost.direction = 0;
            ghost.lastDirectionChange = this.frameCount;
            console.log(`Ghost ${index} eaten! Score: ${this.score}`);
          } else {
            // Lose life
            this.lives--;
            console.log(`Pac-Man hit by ghost ${index}! Lives: ${this.lives}`);
            this.resetPositions();
            if (this.lives <= 0) {
              this.gameOver();
            }
          }
        }
      });
    },
    
    resetPositions() {
      this.pacman.x = 14;
      this.pacman.y = 23;
      this.pacman.direction = 0;
      this.pacman.nextDirection = 0;
      
      this.ghosts[0].x = 13; this.ghosts[0].y = 11; this.ghosts[0].lastDirectionChange = 0;
      this.ghosts[1].x = 14; this.ghosts[1].y = 11; this.ghosts[1].lastDirectionChange = 0;
      this.ghosts[2].x = 15; this.ghosts[2].y = 11; this.ghosts[2].lastDirectionChange = 0;
      this.ghosts[3].x = 16; this.ghosts[3].y = 11; this.ghosts[3].lastDirectionChange = 0;
    },
    
    updateFrightenedMode() {
      if (this.frightenedMode) {
        this.frightenedTimer--;
        if (this.frightenedTimer <= 0) {
          this.frightenedMode = false;
          console.log('Frightened mode ended');
        }
      }
    },
    
    checkLevelComplete() {
      if (this.dots.length === 0 && this.powerPellets.length === 0) {
        this.level++;
        this.initializeDots();
        this.resetPositions();
        this.frightenedMode = false;
        this.frightenedTimer = 0;
      }
    },
    
    gameOver() {
      this.gameRunning = false;
      alert(`Game Over! Final Score: ${this.score}`);
    },
    
    gameLoop() {
      if (!this.gameRunning || this.gamePaused) return;
      
      // Adjust frame skip for balanced speed - change from 3 to 5 for medium speed
      if (this.frameCount % 5 === 0) {
        this.movePacman();
        this.moveGhosts();
        this.checkCollisions();
        this.updateFrightenedMode();
        this.checkLevelComplete();
      }
      
      // Clear canvas
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      
      // Draw everything
      this.drawMaze();
      this.drawDots();
      this.drawPowerPellets();
      this.drawPacman();
      this.drawGhosts();
      
      this.frameCount++;
      this.animationId = requestAnimationFrame(this.gameLoop);
    },
    
    startGame() {
      this.gameRunning = true;
      this.gamePaused = false;
      this.frameCount = 0;
      this.gameLoop();
    },
    
    pauseGame() {
      this.gamePaused = !this.gamePaused;
      if (!this.gamePaused) {
        this.gameLoop();
      }
    },
    
    resetGame() {
      this.gameRunning = false;
      this.gamePaused = false;
      this.score = 0;
      this.lives = 3;
      this.level = 1;
      this.frightenedMode = false;
      this.frightenedTimer = 0;
      this.frameCount = 0;
      
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
      }
      
      this.resetPositions();
      this.initializeDots();
      this.drawMaze();
      this.drawDots();
      this.drawPowerPellets();
      this.drawPacman();
      this.drawGhosts();
    }
  }
};
</script>

<style scoped>
#pacman-game {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: #000;
  color: #fff;
  font-family: 'Courier New', monospace;
}

.game-header {
  display: flex;
  justify-content: space-between;
  width: 448px;
  margin-bottom: 20px;
  font-size: 18px;
  font-weight: bold;
}

.game-container {
  border: 2px solid #0000FF;
  background: #000;
}

.game-controls {
  margin-top: 20px;
  text-align: center;
}

.instructions {
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.5;
}

.instructions p {
  margin: 5px 0;
}

button {
  background: #0000FF;
  color: #fff;
  border: none;
  padding: 10px 20px;
  margin: 0 10px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 5px;
  transition: background 0.3s;
}

button:hover:not(:disabled) {
  background: #0000CC;
}

button:disabled {
  background: #666;
  cursor: not-allowed;
}

canvas {
  display: block;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}
</style>
  